import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PhoneForwarded, Check, CheckCircle, XCircle } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import { Badge, EmptyState, IconButton, LoadingState } from '@/components/ui'

interface Callback {
  id: string
  callerName: string
  callerNumber: string
  reason?: string
  status: 'open' | 'in_progress' | 'done'
  createdAt: string
  assignedTo?: string
  contactReached?: boolean | null
}

export default function CallbacksWidget() {
  const a = useAnonymize()
  const queryClient = useQueryClient()

  const { data: callbacks = [], isLoading } = useQuery({
    queryKey: ['callbacks-open'],
    queryFn: async () => {
      const { data } = await apiClient.get<Callback[]>('/calls/callbacks', {
        params: { status: 'open' },
      })
      return Array.isArray(data) ? data : []
    },
    refetchInterval: 60 * 1000,
  })

  const completeMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.patch(`/calls/callbacks/${id}`, { status: 'done' })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callbacks-open'] })
    },
  })

  if (isLoading) {
    return <LoadingState size="sm" label="" />
  }

  if (callbacks.length === 0) {
    return <EmptyState size="sm" icon={<PhoneForwarded />} title="Keine offenen Rückrufe" />
  }

  return (
    <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
      {callbacks.slice(0, 10).map((cb) => (
        <div key={cb.id} className="flex items-center justify-between p-2 rounded-md bg-gray-50 text-body">
          <div className="min-w-0 flex-1">
            <div className="font-medium text-gray-900 truncate">{a.name(cb.callerName)}</div>
            <div className="text-caption text-gray-500">{a.phone(cb.callerNumber)}</div>
            {cb.reason && <div className="text-caption text-gray-400 truncate">{a.freeText(cb.reason)}</div>}
          </div>
          {cb.contactReached === true ? (
            <Badge variant="success" size="sm" className="mx-2 shrink-0">
              <CheckCircle className="w-3 h-3" />
              Erreicht
            </Badge>
          ) : cb.contactReached === false ? (
            <Badge variant="danger" size="sm" className="mx-2 shrink-0">
              <XCircle className="w-3 h-3" />
              Nicht erreicht
            </Badge>
          ) : null}
          <IconButton
            icon={<Check />}
            aria-label="Als erledigt markieren"
            size="xs"
            variant="ghost"
            onClick={() => completeMutation.mutate(cb.id)}
            loading={completeMutation.isPending}
            className="ml-2 text-green-600 hover:bg-green-100"
          />
        </div>
      ))}
    </div>
  )
}
